import { useState, useCallback } from "react";
import { Drawer, Button } from "@blofin/blofin-ui";
import { useStoreState } from "../store/useStore";
import { useTranslations } from "use-intl";
import clsx from "clsx";
import { cva } from "class-variance-authority";
import { showDepositDrawer } from "@/nav-localized/config";
import DepositFillIcon from "@/nav-localized/assets/header/deposit-fill.svg?react";
import BuyCryptoIcon from "@/nav-localized/assets/header/buy-crypto-fill.svg?react";

// ─── Drawer Content Styles (CVA) ────────────────────────────────

const sectionTitle = cva("text-[14px] leading-[20px]", {
  variants: {
    mode: {
      light: "text-light-label-40",
      dark: "text-dark-label-40"
    }
  }
});

/** Figma: border #2D2D2E dark / #EBEDF0 light, hover bg fill-secondary */
const optionCard = cva(
  "group/option flex items-center gap-[8px] p-[16px] rounded-[16px] border border-solid cursor-pointer transition-colors",
  {
    variants: {
      mode: {
        light: "border-light-line-primary",
        dark: "border-dark-line-primary"
      }
    }
  }
);

/** Figma: bg #1A1A1A dark / #F2F4F5 light */
const optionIconWrapper = cva(
  "flex items-center justify-center w-[40px] h-[40px] rounded-[8px] shrink-0",
  {
    variants: {
      mode: {
        light: "bg-light-fill-secondary",
        dark: "bg-dark-fill-secondary"
      }
    }
  }
);

const optionTitle = cva("text-[16px] leading-[24px] font-medium", {
  variants: {
    mode: {
      light: "text-light-label",
      dark: "text-dark-label"
    }
  }
});

/** Figma: rgba(235,236,245,0.6) dark / rgba(10,10,10,0.6) light */
const optionDesc = cva("text-[14px] leading-[20px]", {
  variants: {
    mode: {
      light: "text-light-label-60",
      dark: "text-dark-label-60"
    }
  }
});

const optionArrow = cva("iconfont icon-arrow-right-l-line text-[20px] shrink-0", {
  variants: {
    mode: {
      light: "text-light-label-40 group-hover/option:text-light-primary",
      dark: "text-dark-label-40 group-hover/option:text-dark-primary"
    }
  }
});

const dividerLine = cva("w-full h-px", {
  variants: {
    mode: {
      light: "bg-light-line-primary",
      dark: "bg-dark-line-primary"
    }
  }
});

const iconColor = cva("", {
  variants: {
    mode: {
      light: "text-light-label",
      dark: "text-dark-label"
    }
  }
});

// ─── Helpers ────────────────────────────────────────────────────

const getIsAssetsRoute = (): boolean => {
  if (typeof window === "undefined") return false;
  const router = window.next?.router;
  if (!router) return false;
  return !!router.asPath?.includes?.("/assets");
};

// ─── Component ──────────────────────────────────────────────────

const DepositDrawer = () => {
  const [open, setOpen] = useState(false);
  const { theme, localeLink, isDemoTrading, userInfo, userAccountType } = useStoreState();
  const t = useTranslations("header");

  const mode = theme === "dark" ? "dark" : "light";
  const isMainAccount = userInfo?.account_type === "MAIN" || userAccountType === "MAIN";

  const handleOpen = useCallback(() => {
    window?.reportTrack?.("Deposit_entry_click", { entry_source: "header deposit" });
    if (!showDepositDrawer) {
      window.location.href = `${localeLink}/assets/deposit`;
      return;
    }
    setOpen(true);
  }, [localeLink]);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleDepositCrypto = useCallback(() => {
    window?.reportTrack?.("Slide_menu_deposit_click", { entry_source: "header deposit" });
    window.location.href = `${localeLink}/assets/deposit`;
    handleClose();
  }, [localeLink, handleClose]);

  const handleBuyCrypto = useCallback(() => {
    window?.reportTrack?.("Slide_menu_buycrypto_click", { entry_source: "header deposit" });
    window.location.href = `${localeLink}/crypto`;
    handleClose();
  }, [localeLink, handleClose]);

  if (isDemoTrading || !isMainAccount || getIsAssetsRoute()) return null;

  return (
    <>
      <Button
        variant="primary"
        size="small"
        theme={theme}
        className="font-medium h-[30px] text-[14px] leading-[20px] my-auto mr-[16px] max-[860px]:hidden"
        onClick={handleOpen}
        id="header_deposit"
      >
        {t("deposit")}
      </Button>

      <Drawer
        title=""
        placement="right"
        theme={theme}
        open={open}
        cancel={handleClose}
        hideIcon
        drawerContentClass="w-[400px] max-[600px]:w-full !pt-0 !pb-0"
        content={
          <div className="flex flex-col gap-[40px]">
            <div className="flex items-center gap-[8px]">
              <p
                className={clsx(
                  "flex-1 text-[16px] leading-[24px] font-medium",
                  mode === "dark" ? "text-dark-label" : "text-light-label"
                )}
              >
                {t("deposit")}
              </p>
              <i
                className={clsx(
                  "iconfont icon-close-line text-[24px] cursor-pointer",
                  mode === "dark" ? "text-dark-label-40" : "text-light-label-40"
                )}
                onClick={handleClose}
              />
            </div>

            <div className="flex flex-col gap-[24px]">
              <div className="flex flex-col gap-[16px]">
                <p className={sectionTitle({ mode })}>{t("depositDrawerHaveCrypto")}</p>
                <div className={optionCard({ mode })} onClick={handleDepositCrypto}>
                  <div className={optionIconWrapper({ mode })}>
                    <DepositFillIcon className={clsx("w-[24px] h-[24px]", iconColor({ mode }))} />
                  </div>
                  <div className="flex flex-col gap-[4px] flex-1 min-w-0">
                    <p className={optionTitle({ mode })}>{t("depositCrypto")}</p>
                    <p className={optionDesc({ mode })}>{t("depositCryptoDesc")}</p>
                  </div>
                  <i className={optionArrow({ mode })} />
                </div>
              </div>

              <div className={dividerLine({ mode })} />

              <div className="flex flex-col gap-[16px]">
                <p className={sectionTitle({ mode })}>{t("depositDrawerNoCrypto")}</p>
                <div className={optionCard({ mode })} onClick={handleBuyCrypto}>
                  <div className={optionIconWrapper({ mode })}>
                    <BuyCryptoIcon className={clsx("w-[24px] h-[24px]", iconColor({ mode }))} />
                  </div>
                  <div className="flex flex-col gap-[4px] flex-1 min-w-0">
                    <p className={optionTitle({ mode })}>{t("depositDrawerBuyCrypto")}</p>
                    <p className={optionDesc({ mode })}>{t("depositDrawerBuyCryptoDesc")}</p>
                  </div>
                  <i className={optionArrow({ mode })} />
                </div>
              </div>
            </div>
          </div>
        }
      />
    </>
  );
};

export default DepositDrawer;
